<template>
  <div>
    <v-dialog
      v-model="showAddDialog"
      eager
      max-width="950px"
    >
      <forms-inventory-counts-add
        :focus-input="showAddDialog"
        @saved="itemAdded($event)"
      />
    </v-dialog>

    <v-row>
      <v-col cols="8">
        <v-text-field
          v-if="includeSearch"
          ref="search"
          v-model="conditions.search"
          :label="$t('Search')"
          class="mb-0"
          clearable
          filled
          hide-details
          prepend-inner-icon="far fa-search"
          single-line
        />
      </v-col>
      <v-col cols="2">
        <v-select
          v-if="includeSearch"
          v-model="conditions.type"
          :items="types"
          :label="$t('Type')"
          class="mb-0"
          clearable
          filled
          hide-details
          item-text="label"
          item-value="code"
          prepend-inner-icon="far fa-box"
        />
      </v-col>
      <v-col cols="2">
        <v-select
          v-if="includeSearch"
          v-model="conditions.status"
          :items="statuses"
          :label="$t('Status')"
          class="mb-0"
          clearable
          filled
          hide-details
          item-text="label"
          item-value="code"
          prepend-inner-icon="far fa-question"
        />
      </v-col>
    </v-row>

    <v-data-table
      v-bind="dataTableProps"
      :headers="headers"
      :items="inventoryCounts"
      :loading="$wait.is('fetching inventorycounts')"
      :options.sync="pagination"
      :server-items-length="nrOfInventoryCounts"
      class="elevation-1 white mb-12"
      sort-by="created"
      sort-desc
    >

      <template v-slot:item="props">
        <tr
          style="cursor: pointer"
          @click="redirectToDetails(props.item.uuid)"
        >
          <td>{{ props.item.name }}</td>
          <td>{{ props.item.created | moment("D MMMM YYYY HH:mm") }}</td>
          <td>{{ $t(getTypeLabel(props.item.type)) }}</td>
          <td>
            <status-chip
              :status-code="props.item.status"
              class="mt-2"
              status-type="inventory-count"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-fab-transition>
      <v-btn
        v-show="!showAddDialog"
        bottom
        color="secondary darken-1"
        dark
        fab
        fixed
        right
        @click="showAddDialog = true"
      >
        <v-icon>fal fa-plus</v-icon>
      </v-btn>
    </v-fab-transition>
  </div>
</template>

<script>
import StatusChip from '@/components/chips/StatusChip'
import FormsInventoryCountsAdd from '@/components/forms/inventory-counts/FormsInventoryCountsAdd'
import inventoryCountService from '@/services/inventory-counts'
import { VDataTableDefaultProps as dataTableProps } from '@/utils/default-props/v-data-table'
import InventoryCountStatuses from '@/utils/statuses/inventory-count'
import { getLabel as getTypeLabel, Types as InventoryCountTypes } from '@/utils/types/inventory-count'
import camelcaseKeys from 'camelcase-keys'
import { mapGetters } from 'vuex'

export default {
  name: 'InventoryCountsTable',

  components: {
    FormsInventoryCountsAdd,
    StatusChip
  },

  props: {
    includeSearch: {
      default: false,
      type: Boolean
    }
  },

  data () {
    return {
      conditions: {},
      dataTableProps,
      headers: [
        {
          text: this.$t('Name'),
          value: 'name'
        },
        {
          text: this.$t('Date'),
          value: 'created'
        },
        {
          text: this.$t('Type'),
          value: 'type',
          sortable: false
        },
        {
          text: this.$t('Status'),
          value: 'status',
          width: '50px',
          sortable: false
        }
      ],
      inventoryCounts: [],
      nrOfInventoryCounts: 0,
      pagination: {},
      showAddDialog: false,
      statuses: InventoryCountStatuses,
      types: InventoryCountTypes
    }
  },

  computed: {
    ...mapGetters('AUTH', ['user'])
  },

  watch: {
    // fetch inventoryCounts when requested page or sorting changed
    pagination (value) {
      this.updateResults()
    },
    // fetch receipts when search has changed
    'conditions.search': function (search) {
      this.updateResultsDebounced()
    },
    'conditions.status': function (search) {
      this.updateResults()
    },
    'conditions.type': function (search) {
      this.updateResults()
    },
    // Translate the status labels when user language has been changed
    // Imported labels are not reactive so we need this watcher
    user: {
      immediate: true,
      handler (data) {
        this.statuses = InventoryCountStatuses.map(x => {
          x.label = this.$t(x.label)
          return x
        })
        this.types = InventoryCountTypes.map(x => {
          x.label = this.$t(x.label)
          return x
        })
      }
    }
  },

  methods: {
    getTypeLabel (typeCode) {
      return getTypeLabel(typeCode)
    },
    redirectToDetails (uuid) {
      this.$router.push({ name: 'InventoryCountsCount', params: { uuid } })
    },
    itemAdded (item) {
      this.updateResults()
      this.redirectToDetails(item.uuid)
    },
    updateResultsDebounced: _.debounce(function () {
      this.updateResults()
    }, 500),
    updateResults () {
      this.$wait.start('fetching inventorycounts')
      // reset dataset
      this.inventoryCounts = []

      inventoryCountService.pagination(this.conditions, this.pagination)
        .then((data) => {
          if (data.data) {
            this.inventoryCounts = camelcaseKeys(data.data.inventory_counts, { deep: true })
            this.nrOfInventoryCounts = data.data.total_count
          }
        })
        .finally((data) => this.$wait.end('fetching inventorycounts'))
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/inventory-counts/InventoryCountsTable.vue